import { ExternalLink, RotateCcw } from 'lucide-react';

import { downloadUrl } from '@/api/client';
import type { Sale } from '@/api/types';
import { Can } from '@/auth/guards';
import { PERMISSIONS } from '@/auth/permissions';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { formatDateTime, money } from '@/lib/utils';

export function SaleCard({ sale, onReturn }: { sale: Sale; onReturn: () => void }) {
  const itemsSummary = sale.items.map((i) => `${i.medicineName} ×${i.quantity}`).join(' · ') || '—';

  return (
    <Card className="p-3.5">
      <div className="mb-1.5 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-semibold">{sale.invoiceNo}</span>
          <Badge variant={sale.isReturn ? 'out' : 'ok'}>{sale.isReturn ? 'Return' : sale.paymentMode}</Badge>
        </div>
        <span className="tabular text-lg font-bold text-primary">{money(sale.totalAmount)}</span>
      </div>

      <p className="mb-1.5 text-xs text-muted-foreground">
        {sale.customerName || 'Walk-in'}
        {sale.customerPhone && ` · ${sale.customerPhone}`}
        {' · '}
        {formatDateTime(sale.createdAt)}
      </p>

      <p className="mb-2.5 truncate rounded-md bg-secondary/50 px-2 py-1.5 text-xs text-muted-foreground">
        {itemsSummary}
      </p>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" asChild>
          <a href={downloadUrl(`/api/bills/${sale.invoiceNo}/pdf`)} target="_blank" rel="noreferrer">
            <ExternalLink /> Bill
          </a>
        </Button>
        {!sale.isReturn && (
          <Can permission={PERMISSIONS.salesRefund}>
            <Button variant="outline" size="sm" onClick={onReturn}>
              <RotateCcw /> Return
            </Button>
          </Can>
        )}
      </div>
    </Card>
  );
}